/**
 * The socket exactly as it stood **before Circumference Modification was
 * applied** — the whole mesh, which is what the measuring tools call "Scan".
 *
 * The whole mesh, deliberately: the per-ring numbers the apply records only
 * answer for the heights those rings happened to sit at, and the measuring tools
 * let the user put a line anywhere. Keeping the geometry means Scan can be
 * sliced at any height, before and after a reload.
 *
 * Why this is not the compare baseline: the compare baseline is the model as it
 * was *loaded*, and it is not always even that. On a resume the scene is rebuilt
 * from a checkpoint, and when no load-time baseline survives in memory
 * (`ThreeScene`) the overlay is cloned from the checkpoint mesh — a socket that
 * already carries the reduction. Measuring "Scan" off that mesh reported the
 * modified socket in both columns, which is the bug this snapshot exists to
 * close.
 *
 * Two layers:
 *
 *   - a module ref, for the session that did the apply. The geometry is held by
 *     reference, not cloned: the undo command already keeps the same pre-apply
 *     geometry alive for its own restore, and `dispose()` only releases GPU
 *     buffers — the position attribute a slice reads stays intact.
 *   - one IndexedDB record per design, so the mesh is still there after a reload.
 *     A separate database from the undo blobs and the scene cache, for the reason
 *     spelled out in `beforeMeshCache`: a failed upgrade in one must not take out
 *     the other. Every path is defensive — private mode, missing IDB and quota
 *     exhaustion all degrade to "no persisted mesh" rather than throwing into an
 *     apply or a render.
 *
 * Lifetime: written when the measure-reduction command executes, dropped when
 * that apply is undone or reset, and dropped again if some later operation bakes
 * a new local frame into the socket (the stored geometry would then be in a frame
 * that no longer registers with the socket — the compare baseline is re-framed
 * for that case, this snapshot is not).
 */
import * as THREE from 'three';

import { packMeshBlob, unpackMeshBlob } from '../../features/step-checkpoints/undo/meshBlob';
import { loadBkDesignOrderContext } from './bkDesignOrderContext';

const LOG_PREFIX = '[PreModificationSnapshot]';

const DB_NAME = 'benx-scan-reference';
const DB_VERSION = 1;
const STORE = 'blobs';

/** One record per design; this only bites after a long run of them. */
const SOFT_BUDGET_BYTES = 150 * 1024 * 1024;

/** @type {{ geometry: import('three').BufferGeometry } | null} */
let snapshot = null;

/** @returns {string | null} one record per design. */
function orderKey() {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return null;
  return `${ctx.sales_order}:${ctx.customer}`;
}

function openDb() {
  return new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      console.warn(`${LOG_PREFIX} indexedDB unavailable`, e);
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn(`${LOG_PREFIX} open failed`, req.error);
      resolve(null);
    };
    req.onblocked = () => resolve(null);
  });
}

/**
 * Run one transaction. `body` gets the store and a `done` callback; whatever it
 * passes to `done` (or returns) resolves the promise.
 */
async function withStore(mode, body) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    let tx;
    try {
      tx = db.transaction(STORE, mode);
    } catch (e) {
      console.warn(`${LOG_PREFIX} transaction failed`, e);
      db.close();
      finish(null);
      return;
    }
    const store = tx.objectStore(STORE);
    let result;
    try {
      result = body(store, finish);
    } catch (e) {
      console.warn(`${LOG_PREFIX} store op failed`, e);
    }
    tx.oncomplete = () => { db.close(); finish(result ?? null); };
    tx.onerror = () => { db.close(); finish(null); };
    tx.onabort = () => { db.close(); finish(null); };
  });
}

/**
 * Drop the oldest records until the store is back under budget. One record per
 * design, so this only ever bites after a long run of different designs on the
 * same device.
 */
function pruneToBudget() {
  return withStore('readwrite', (store, done) => {
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = (req.result ?? []).sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
      let total = rows.reduce((sum, r) => sum + (r.bytes ?? 0), 0);
      while (total > SOFT_BUDGET_BYTES && rows.length > 1) {
        const oldest = rows.shift();
        total -= oldest.bytes ?? 0;
        store.delete(oldest.key);
      }
      done(true);
    };
    req.onerror = () => done(null);
    return null;
  });
}

/** Pack and store, off the apply's critical path. */
function persist(mesh) {
  const key = orderKey();
  if (!key) return; // local-only design: the module ref covers this session
  const payload = {
    geometry: mesh.geometry,
    position: mesh.position.toArray(),
    quaternion: mesh.quaternion.toArray(),
    scale: mesh.scale.toArray(),
  };
  // Packing walks the whole geometry — give the renderer a frame first so the
  // apply that just finished still paints smoothly.
  const run = () => {
    let buffer;
    try {
      buffer = packMeshBlob(payload);
    } catch (e) {
      console.warn(`${LOG_PREFIX} pack failed`, e);
      return;
    }
    if (!buffer) return;
    withStore('readwrite', (store) => {
      store.put({ key, buffer, bytes: buffer.byteLength, createdAt: Date.now() });
      return true;
    }).then((ok) => {
      if (!ok) console.warn(`${LOG_PREFIX} write failed — Scan falls back after a reload`);
      else pruneToBudget();
    });
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else run();
}

/**
 * Remember the socket that is about to be replaced by the reduced one.
 *
 * @param {import('three').Mesh} mesh Socket as it stands before the apply.
 * @returns {boolean}
 */
export function capturePreModificationSocket(mesh) {
  if (!mesh?.geometry?.attributes?.position?.count) return false;
  snapshot = { geometry: mesh.geometry };
  persist(mesh);
  return true;
}

/** @returns {boolean} true while a pre-modification socket is in memory. */
export function hasPreModificationSocket() {
  return !!snapshot?.geometry?.attributes?.position?.count;
}

/**
 * Pull the persisted mesh back into memory — for a session that did not do the
 * apply itself (a resume, a reload). No-op when one is already in memory.
 *
 * @returns {Promise<boolean>} whether a mesh is available afterwards.
 */
export async function hydratePreModificationSocket() {
  if (hasPreModificationSocket()) return true;
  const key = orderKey();
  if (!key) return false;

  const buffer = await withStore('readonly', (store, done) => {
    const req = store.get(key);
    req.onsuccess = () => done(req.result?.buffer ?? null);
    req.onerror = () => done(null);
    return null;
  });
  if (!buffer) return false;

  const payload = unpackMeshBlob(buffer);
  if (!payload?.geometry?.attributes?.position?.count) {
    console.warn(`${LOG_PREFIX} stored blob did not unpack`);
    return false;
  }
  // The TRS is not restored with it: callers place the mesh on the socket's
  // current transform, which is where the comparison has to happen anyway.
  snapshot = { geometry: payload.geometry };
  return true;
}

export function clearPreModificationSocket() {
  snapshot = null;
  const key = orderKey();
  if (!key) return;
  withStore('readwrite', (store) => {
    store.delete(key);
    return true;
  });
}

/**
 * A bare mesh over the recorded geometry, for slicing only — it is never added
 * to the scene and carries no material worth speaking of.
 *
 * The caller owns the returned mesh but NOT its geometry (shared with the undo
 * command), so drop the mesh and never dispose the geometry.
 *
 * Callers place it: the recorded geometry is in the socket's local frame, so it
 * has to be given the socket's current TRS before it is measured, the same way
 * the compare overlay is pinned to the socket every frame.
 *
 * @returns {import('three').Mesh | null}
 */
export function instantiatePreModificationMesh() {
  if (!hasPreModificationSocket()) return null;
  const mesh = new THREE.Mesh(snapshot.geometry);
  mesh.name = 'PreModificationSocket';
  mesh.matrixAutoUpdate = true;
  return mesh;
}
